/**
 * Control-point write queue (SPEC §4.1):
 * - at most one outstanding write; the next one is sent only after the response/indication (or a timeout),
 * - coalescing: per command key only the latest pending payload is kept,
 * - per-key rate limit (minimum interval between sends of the same key).
 */

export type WriteResult =
  | { ok: true }
  | { ok: false; reason: 'timeout' | 'rejected' | 'error' | 'cleared'; code?: number; error?: unknown };

export interface EnqueueOptions {
  /** Minimum time between two sends of this key (ms). */
  minIntervalMs?: number;
  /** Value the response must carry to be matched to this command (e.g. the request op code). */
  responseKey?: number;
  /** Do not wait for a response for this command. */
  noResponse?: boolean;
  /** Higher runs first among ready commands (default 0). */
  priority?: number;
}

export interface WriteQueueOptions {
  send: (bytes: Uint8Array) => Promise<void>;
  /** Wait for `handleResponse` before sending the next command. */
  awaitResponse: boolean;
  responseTimeoutMs?: number;
  onLog?: (msg: string) => void;
}

interface Pending {
  key: string;
  bytes: Uint8Array;
  opts: EnqueueOptions;
  waiters: ((r: WriteResult) => void)[];
}

interface InFlight {
  pending: Pending;
  timer: ReturnType<typeof setTimeout> | null;
}

export class WriteQueue {
  private readonly pending = new Map<string, Pending>();
  private readonly lastSentAt = new Map<string, number>();
  private inFlight: InFlight | null = null;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;
  private sending = false;
  private readonly responseTimeoutMs: number;

  constructor(private readonly options: WriteQueueOptions) {
    this.responseTimeoutMs = options.responseTimeoutMs ?? 1500;
  }

  get size(): number {
    return this.pending.size + (this.inFlight ? 1 : 0);
  }

  get busy(): boolean {
    return this.inFlight !== null || this.sending;
  }

  enqueue(key: string, bytes: Uint8Array, opts: EnqueueOptions = {}): Promise<WriteResult> {
    return new Promise((resolve) => {
      const existing = this.pending.get(key);
      if (existing) {
        existing.bytes = bytes;
        existing.opts = opts;
        existing.waiters.push(resolve);
      } else {
        this.pending.set(key, { key, bytes, opts, waiters: [resolve] });
      }
      this.pump();
    });
  }

  /** Feed a response from the device. `responseKey` is matched against the in-flight command. */
  handleResponse(responseKey: number, ok: boolean, code?: number): void {
    const f = this.inFlight;
    if (!f) return;
    const expected = f.pending.opts.responseKey;
    if (expected !== undefined && expected !== responseKey) return;
    if (f.timer) clearTimeout(f.timer);
    this.inFlight = null;
    this.finish(f.pending, ok ? { ok: true } : { ok: false, reason: 'rejected', code });
    this.pump();
  }

  /** Drop everything (e.g. on disconnect). */
  clear(): void {
    if (this.wakeTimer) clearTimeout(this.wakeTimer);
    this.wakeTimer = null;
    if (this.inFlight) {
      if (this.inFlight.timer) clearTimeout(this.inFlight.timer);
      this.finish(this.inFlight.pending, { ok: false, reason: 'cleared' });
      this.inFlight = null;
    }
    for (const p of this.pending.values()) this.finish(p, { ok: false, reason: 'cleared' });
    this.pending.clear();
    this.lastSentAt.clear();
  }

  private finish(p: Pending, r: WriteResult): void {
    for (const w of p.waiters) w(r);
  }

  private pump(): void {
    if (this.inFlight || this.sending) return;
    if (this.pending.size === 0) return;
    const now = Date.now();
    let earliest = Infinity;
    let best: Pending | null = null;
    for (const p of this.pending.values()) {
      const last = this.lastSentAt.get(p.key) ?? -Infinity;
      const readyAt = last + (p.opts.minIntervalMs ?? 0);
      if (readyAt <= now) {
        if (!best || (p.opts.priority ?? 0) > (best.opts.priority ?? 0)) best = p;
      } else {
        earliest = Math.min(earliest, readyAt);
      }
    }
    if (best) {
      this.pending.delete(best.key);
      void this.send(best);
      return;
    }
    if (earliest < Infinity && !this.wakeTimer) {
      this.wakeTimer = setTimeout(
        () => {
          this.wakeTimer = null;
          this.pump();
        },
        Math.max(0, earliest - now),
      );
    }
  }

  private async send(p: Pending): Promise<void> {
    const wantsResponse = this.options.awaitResponse && !p.opts.noResponse;
    const inflight: InFlight = { pending: p, timer: null };
    // Register before writing: the indication may arrive before the write promise settles.
    if (wantsResponse) this.inFlight = inflight;
    this.sending = true;
    this.lastSentAt.set(p.key, Date.now());
    try {
      await this.options.send(p.bytes);
    } catch (error) {
      this.sending = false;
      if (this.inFlight === inflight) this.inFlight = null;
      this.options.onLog?.(`write ${p.key} failed: ${String(error)}`);
      this.finish(p, { ok: false, reason: 'error', error });
      this.pump();
      return;
    }
    this.sending = false;
    if (!wantsResponse) {
      this.finish(p, { ok: true });
      this.pump();
      return;
    }
    if (this.inFlight !== inflight) {
      // Response already handled (or queue cleared) while the write was in progress.
      this.pump();
      return;
    }
    inflight.timer = setTimeout(() => {
      if (this.inFlight !== inflight) return;
      this.inFlight = null;
      this.options.onLog?.(`write ${p.key}: response timeout`);
      this.finish(p, { ok: false, reason: 'timeout' });
      this.pump();
    }, this.responseTimeoutMs);
  }
}
